import { useState } from 'react';
import { Box } from '@mui/material';
import type { TeamMapping } from '../../types';

export interface TeamLogoProps {
  /** Team mapping data (optional — renders fallback if missing or logo fails) */
  mapping?: TeamMapping;
  /** Fallback color when mapping has no primaryColor */
  fallbackColor?: string;
  /** Logo size in pixels */
  size?: number;
  /** Alt text override (defaults to mapping.teamName) */
  alt?: string;
}

/**
 * Renders a team logo image with graceful fallback to a colored dot.
 * If the logo URL fails to load, falls back to primaryColor dot.
 * If no primaryColor, falls back to fallbackColor dot.
 * If neither, renders nothing.
 */
export const TeamLogo: React.FC<TeamLogoProps> = ({
  mapping,
  fallbackColor,
  size = 24,
  alt,
}) => {
  const [imgError, setImgError] = useState(false);
  const logoUrl = mapping?.logoUrl;
  const dotColor = mapping?.primaryColor || fallbackColor;

  if (logoUrl && !imgError) {
    return (
      <Box
        component="img"
        src={logoUrl}
        alt={alt || mapping?.teamName || ''}
        onError={() => setImgError(true)}
        sx={{ width: size, height: size, objectFit: 'contain', flexShrink: 0 }}
      />
    );
  }

  if (dotColor) {
    return (
      <Box
        sx={{
          width: size * 0.4,
          height: size * 0.4,
          borderRadius: '50%',
          bgcolor: dotColor,
          flexShrink: 0,
        }}
      />
    );
  }

  return null;
};
